import { useCallback, useEffect, useRef, useState } from 'react';
import { captureRadioInteraction, clearRadioInteraction, clearPendingRadioInteraction, parseRadioInvitation,
  RADIO_CONTROLS_EVENT, RADIO_INVITATION_EVENT } from './radioParticipationIntent';

// Web Locks provides affirmative exclusivity among updated same-origin tabs.
// We never infer permission from an expired heartbeat or a timed-out tab.
export function useRadioAudioOwnership() {
  const held = useRef(null);
  const epoch = useRef(0);
  const release = useCallback(() => { epoch.current++; held.current?.(); held.current = null; }, []);
  useEffect(() => () => release(), []);
  const claim = useCallback(async () => {
    const requestedEpoch = epoch.current;
    if (held.current) return true;
    if (!navigator.locks?.request) return true; // Scope is disclosed: unsupported browsers remain manual.
    return new Promise(resolve => {
      navigator.locks.request('jg-radio-playing-v1', { ifAvailable: true }, lock => {
        if (!lock || requestedEpoch !== epoch.current) { resolve(false); return undefined; }
        return new Promise(unlock => { held.current = unlock; resolve(true); });
      }).catch(() => resolve(false));
    });
  }, []);
  return { claim, release };
}

export default function useRadioParticipationOwner({ enabled, playerToken, location, history,
  isPlaying, trackId, tracks, loaded, open, stage, participationOpen = false, isDock = false }) {
  const [invitation, setInvitation] = useState(null);
  const state = useRef(null);
  state.current = { enabled, playerToken, isPlaying, trackId, tracks, loaded, open, stage, participationOpen };
  const consumedRoute = useRef('');
  const handled = useRef(new Set());
  useEffect(() => {
    setInvitation(null); if (state.current.enabled) clearRadioInteraction(); handled.current.clear();
  }, [playerToken]);
  useEffect(() => { setInvitation(null); if (state.current.enabled) clearPendingRadioInteraction(); }, [location.pathname]);

  const present = intent => {
    const s = state.current;
    if (!s.enabled || s.participationOpen || !intent || (intent.capsule && !isDock)) return false;
    setInvitation({ ...intent, stoppedWhenPresented: !s.isPlaying });
    s.open();
    void captureRadioInteraction({ entrySurface: intent.entrySurface,
      trackId: intent.trackId, invitation: intent.invitation, playerToken: s.playerToken,
      returnTo: intent.capsule ? intent.url : undefined });
    return true;
  };
  useEffect(() => {
    const controls = event => {
      const s = state.current;
      if (!s.enabled || !isDock || s.participationOpen ||
          !['capsule', 'intake', 'post_submission'].includes(event.detail?.entrySurface)) return;
      s.open();
      void captureRadioInteraction({ entrySurface: event.detail.entrySurface, playerToken: s.playerToken });
    };
    const invitationEvent = event => {
      const parsed = parseRadioInvitation(event.detail?.url);
      const s = state.current;
      if (s.enabled && (s.participationOpen || (!isDock && s.isPlaying))) event.detail.preserveRoute = true;
      if (parsed && !event.detail.accepted) event.detail.accepted = present(parsed);
    };
    const message = event => {
      const data = event.data;
      if (!['JG_RADIO_PROBE_V1', 'JG_RADIO_PRESENT_V1'].includes(data?.type) ||
          typeof data.requestId !== 'string' || data.requestId.length > 100 || !event.ports?.[0]) return;
      const s = state.current;
      if (!s.enabled) return; // A hidden dock must not race the mounted public player to reply.
      const reply = value => event.ports[0].postMessage({ requestId: data.requestId, ...value });
      if (data.type === 'JG_RADIO_PROBE_V1') {
        const parsed = parseRadioInvitation(data.url);
        reply({ capable: Boolean(parsed && !s.participationOpen && (!parsed.capsule || isDock)), playing: Boolean(s.isPlaying) }); return;
      }
      const parsed = parseRadioInvitation(data.url);
      if (!parsed || handled.current.has(data.requestId)) { reply({ accepted: false }); return; }
      handled.current.add(data.requestId);
      if (handled.current.size > 64) handled.current.delete(handled.current.values().next().value);
      reply({ accepted: present(parsed) });
    };
    window.addEventListener(RADIO_CONTROLS_EVENT, controls);
    window.addEventListener(RADIO_INVITATION_EVENT, invitationEvent);
    navigator.serviceWorker?.addEventListener('message', message);
    return () => {
      window.removeEventListener(RADIO_CONTROLS_EVENT, controls);
      window.removeEventListener(RADIO_INVITATION_EVENT, invitationEvent);
      navigator.serviceWorker?.removeEventListener('message', message);
    };
  }, [isDock]);

  useEffect(() => {
    const s = state.current;
    if (!s.enabled || s.participationOpen) return;
    const route = `${location.pathname}${location.search}`;
    if (consumedRoute.current === route) return;
    const query = new URLSearchParams(location.search);
    if (isDock && location.pathname === '/home' && query.get('radio') === '1' && query.get('source') === 'hitlab-submitted') {
      consumedRoute.current = route;
      s.open();
      void captureRadioInteraction({ entrySurface: 'post_submission', playerToken: s.playerToken });
      query.delete('radio'); query.delete('source');
      history.replace({ ...location, search: query.toString() ? `?${query}` : '' });
    } else {
      const parsed = parseRadioInvitation(route);
      if (parsed && (isDock ? parsed.capsule : !parsed.capsule)) {
        consumedRoute.current = route;
        present(parsed);
      }
    }
  }, [enabled, history, isDock, location, participationOpen]);

  const invited = tracks?.find(t => String(t.trackId || t.id) === invitation?.trackId);
  const switchRequired = Boolean(invitation && isPlaying && trackId !== invitation.trackId);
  useEffect(() => {
    if (!invitation || !loaded || !invited || switchRequired) return;
    if ((invitation.stoppedWhenPresented && !isPlaying) || trackId !== invitation.trackId) state.current.stage(invitation.trackId);
    setInvitation(null);
  }, [invitation, invited, loaded, switchRequired, trackId, isPlaying]);
  return { invitation, unavailable: Boolean(invitation && loaded && !invited), switchRequired,
    switchRecord: () => { if (invited) state.current.stage(invitation.trackId); setInvitation(null); },
    dismiss: () => { setInvitation(null); clearRadioInteraction(); } };
}
